<template>
    <toolbar-option type="stacked">
        <template slot="primary">
            <div class="image-control">
                <button
                    v-if="hasImage"
                    class="image-control__remove"
                    @click="removeImage"
                >
                    <font-awesome-icon class="image-control__remove-icon" :icon="['fal', 'trash-alt']" />
                    Remove Image
                </button>

                <div class="image-control__text">
                    <span class="image-control__label">
                        File Name:
                    </span>

                    <span class="image-control__input">
                        {{ filename }}
                    </span>
                </div>
            </div>
            <div class="image-control__browse">
                <button class="image-control__button" @click="openMediaLibrary">
                    Browse
                </button>
                <button v-if="hasDAM" class="image-control__button" @click="openDAM">
                    Browse DAM
                </button>
            </div>
        </template>
    </toolbar-option>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import baseOption from '@/options/mixins/base-option';
import { drawerHelperMixin, TYPE_IMAGE } from '@/mixins/drawer-helper';
import { SET_SELECTED_MEDIA_TARGET, SHOW_DRAWER } from '@/store/types/mutation-types';
import mediaTargets from '@/data/constants/media-targets';

export default {
    name: 'background-image',
    mixins: [baseOption, drawerHelperMixin],

    data() {
        return {
            displayName: 'Current Image',
            filename: 'N/A',
        };
    },

    computed: {
        ...mapState({
            selectedMedia: state => state.page.selectedMedia,
            selectedMediaTarget: state => state.page.selectedMediaTarget,
        }),

        hasDAM() {
            return process.env.VUE_APP_CH && process.env.VUE_APP_CH !== '#{ch_server}#';
        },

        hasImage() {
            return this.selectedElements[0].hasElementData('background-image-url');
        },
    },

    watch: {
        selectedMedia() {
            if (this.selectedMediaTarget !== mediaTargets.IMAGE) return;

            if (this.selectedMedia.chid) {
                // content hub images are always absolute CDN urls
                this.selectedElements[0].setElementData('background-image-url', this.selectedMedia.url);
            } else {
                this.selectedElements[0].setElementData('background-image-url', `${ this.getRelativeUrl(this.selectedMedia.url) }`);
            }

            this.selectedElements[0].setElementData('background-size', 'cover');
            this.filename = this.getFileName(this.selectedMedia.url);
            this.onOptionChange(this.selectedMedia.url);
        },
    },

    methods: {
        ...mapMutations({
            setSelectedMediaTarget: SET_SELECTED_MEDIA_TARGET,
            showDrawer: SHOW_DRAWER,
        }),

        getFileName(src) {
            if (!src) return '';

            let filename = src.slice(src.lastIndexOf('/') + 1);
            const queryStringIndex = filename.indexOf('?');
            const maxLength = 15;

            if (queryStringIndex > -1) filename = filename.slice(0, queryStringIndex);
            if (filename.length > maxLength) filename = `...${ filename.slice(filename.length - maxLength) }`;

            return filename;
        },

        openMediaLibrary() {
            this.openMediaDrawer(TYPE_IMAGE);
            this.setSelectedMediaTarget(mediaTargets.IMAGE);
        },

        openDAM() {
            this.showDrawer('content-hub-drawer');
            this.setSelectedMediaTarget(mediaTargets.IMAGE);
        },

        onSelectedElement() {
            const backgroundImage = this.selectedElements[0].getElementData('background-image-url');

            this.filename = this.getFileName(backgroundImage) || 'N/A';
        },

        removeImage() {
            this.selectedElements[0].removeElementData('background-image-url');
            this.selectedElements[0].removeElementData('background-position');
            this.selectedElements[0].removeElementData('background-size');
            this.selectedElements[0].removeElementData('background-overlay');
            this.selectedElements[0].removeElementData('background-overlay-opacity');
            this.selectedElements[0].removeElementData('background-repeat');
            this.filename = 'N/A';
        },

        getRelativeUrl(url) {
            const a = document.createElement('a');
            a.href = url;

            return `${ a.pathname }${ a.search }`;
        },
    },
};
</script>

<style lang="scss" scoped>
.image-control {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    position: relative;

    &__remove {
        position: absolute;
        top: -1.75rem;
        right: 0;
        font-size: 11px;
        color: var(--form-deleteLinkColor);

        &:hover {
            color: var(--form-deleteLinkHoverColor);
        }
    }

    &__remove-icon {
        margin-right: 3px;
    }

    &__label {
        display: block;
        font-size: 12px;
        margin-top: 5px;
    }

    &__input {
        line-height: 1.4;
        width: 100%;
        font-size: 14px;
    }

    &__button {
        @extend %button;
        font-size: 13px;
        padding: 6px 24px;
        min-width: inherit;
    }

    &__browse {
        width: 100%;
        margin-top: 10px;

        button {
            margin-right: 15px;
        }
    }
}
</style>
